import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges
} from '@angular/core';

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  GridOptions,
  SelectionChangedEvent
} from 'ag-grid-community';

import { ButtonCellRendererComponent } from '../table-components/button-cell-renderer/button-cell-renderer.component';
import { Header } from '@aptsi-types';
import { DatePipe } from '@angular/common';
import { RowTooltipComponent } from './row-tooltip.component';

type RowButtonConfig = { [key: string]: boolean };
type RowButtonConfigInput = RowButtonConfig | ((row: any) => RowButtonConfig);

@Component({
  selector: 'app-main-table',
  templateUrl: './main-table.component.html',
  styleUrls: ['./main-table.component.scss'],
  providers: [DatePipe]
})
export class MainTableComponent {
  @Input() headers: Header[] = [];
  @Input() data: any[] = [];
  @Input() columnDefs: ColDef[] = [];
  @Input() buttonConfig: RowButtonConfigInput = {};

  @Input() enableRowSelection = false;
  @Input() rowSelectionType: 'single' | 'multiple' = 'single';
  @Input() selectedRowKeys: string[] = [];
  @Input() rowKey = 'id';

  @Output() actionClicked = new EventEmitter<{
    action: string;
    rowIndex: number;
    rowData: any;
  }>();

  @Output() selectedRowsChange = new EventEmitter<any[]>();

  private gridApi?: GridApi;

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 200,
    filter: true,
    tooltipValueGetter: params => params.data,
    tooltipComponent: 'rowTooltip',
    tooltipComponentParams: {
      headers: this.headers
    }
  };

gridOptions: GridOptions = {
  enableBrowserTooltips: false,
  tooltipShowDelay: 600,
  tooltipHideDelay: 10000,
  tooltipInteraction: true,
  enableCellTextSelection: true,
  suppressDragLeaveHidesColumns: true,

  rowSelection: undefined,

  // Configure the automatically generated checkbox column.
  selectionColumnDef: {
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    flex: 0,
    pinned: 'left',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true
  },

  components: {
    buttonCellRenderer: ButtonCellRendererComponent,
    rowTooltip: RowTooltipComponent
  },

  onSelectionChanged: event =>
    this.selectedRowsChange.emit(event.api.getSelectedRows())
};

  constructor(private readonly datePipe: DatePipe) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['headers'] ||
      changes['enableRowSelection'] ||
      changes['rowSelectionType'] ||
      changes['buttonConfig'] ||
      changes['data']
    ) {
      this.rebuildColumns();
    }

    if (changes['selectedRowKeys'] || changes['data']) {
      setTimeout(() => this.applyPreSelection());
    }
  }

private rebuildColumns(): void {
  const updatedColumnDefs: ColDef[] = [];

  // Determine whether the Actions column is required.
  const hasButtons =
    typeof this.buttonConfig === 'function'
      ? true
      : Object.values(this.buttonConfig).some(Boolean);

  // --------------------------------------------------
  // 1. Actions column
  // --------------------------------------------------
  if (hasButtons) {
    updatedColumnDefs.push({
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: 'buttonCellRenderer',

      cellRendererParams: (params: any) => ({
        buttonConfig: this.getButtonConfigForRow(params?.data),
        onActionClicked: this.onActionClicked.bind(this)
      }),

      sortable: false,
      filter: false,

      // Fixed width for Actions.
      width: 250,
      minWidth: 250,
      maxWidth: 250,
      flex: 0,
      suppressSizeToFit: true
    });
  }

  // --------------------------------------------------
  // 2. Checkbox selection column
  // --------------------------------------------------
  const rowSelection: GridOptions['rowSelection'] =
    this.enableRowSelection
      ? {
          mode:
            this.rowSelectionType === 'multiple'
              ? 'multiRow'
              : 'singleRow',
          checkboxes: true,
          headerCheckbox: this.rowSelectionType === 'multiple'
        }
      : undefined;

  this.gridOptions.rowSelection = rowSelection;

  // Update selection configuration if the grid already exists.
  if (this.gridApi) {
    this.gridApi.setGridOption('rowSelection', rowSelection);
  }

  // --------------------------------------------------
  // 3. Data columns
  // --------------------------------------------------
  this.headers.forEach(header => {
    updatedColumnDefs.push({
      headerName: header.name,
      field: header.property,

      tooltipComponentParams: {
        headers: this.headers
      },

      ...(header.property === 'updatedAt'
        ? {
            valueFormatter: params => {
              if (
                params.value === null ||
                params.value === undefined ||
                params.value === ''
              ) {
                return '—';
              }

              return (
                this.datePipe.transform(
                  params.value,
                  'dd MMM yyyy, h:mm a'
                ) ?? '—'
              );
            }
          }
        : {})
    });
  });

  // Apply column definitions.
  this.columnDefs = updatedColumnDefs;

  if (this.gridApi) {
    this.gridApi.setGridOption('columnDefs', this.columnDefs);
    this.gridApi.refreshHeader();
    this.gridApi.refreshCells({ force: true });
  }
}

  /**
   * Resolve the shared or row-specific button configuration. For data-mapping
   * rows, Map is shown only when status is not MAPPED, and Update only when it
   * is MAPPED. Other tables retain their configured button behavior.
   */
  // private getButtonConfigForRow(row: any): RowButtonConfig {
  //   const configuredButtons: RowButtonConfig = typeof this.buttonConfig === 'function'
  //     ? (this.buttonConfig(row) ?? {})
  //     : this.buttonConfig;

  //   const rowButtonConfig: RowButtonConfig = { ...configuredButtons };

  //   if (!row || typeof row !== 'object') {
  //     return rowButtonConfig;
  //   }

  //   // Also recognize the empty-key fallback from the API sample.
  //   const hasMappingStatus =
  //     Object.prototype.hasOwnProperty.call(row, 'mappingStatus') ||
  //     Object.prototype.hasOwnProperty.call(row, 'mapping_status') ||
  //     Object.prototype.hasOwnProperty.call(row, '');

  //   // Do not alter the action rules for unrelated tables.
  //   if (!hasMappingStatus) {
  //     return rowButtonConfig;
  //   }

  //   const status = String(
  //     row['mappingStatus'] ?? row['mapping_status'] ?? row[''] ?? ''
  //   ).trim().toUpperCase();


  //   if (Object.prototype.hasOwnProperty.call(rowButtonConfig, 'map')) {
  //     rowButtonConfig['map'] = Boolean(rowButtonConfig['map']) && status !== 'MAPPED';
  //   }

  //   if (Object.prototype.hasOwnProperty.call(rowButtonConfig, 'view')) {
  //     const viewEnabled = Boolean(rowButtonConfig['view']);
  //     // Show view when status is MAPPED, OR when status is null/empty
  //     rowButtonConfig['view'] = viewEnabled && (status === 'MAPPED' || status === '');
  //   }

  //   if (Object.prototype.hasOwnProperty.call(rowButtonConfig, 'update')) {
  //     rowButtonConfig['update'] = Boolean(rowButtonConfig['update']) && status === 'MAPPED';
  //   }

  //   return rowButtonConfig;
  // }

  private getButtonConfigForRow(row: any): RowButtonConfig {
  const configuredButtons: RowButtonConfig =
    typeof this.buttonConfig === 'function'
      ? this.buttonConfig(row) ?? {}
      : this.buttonConfig;

  return {
    ...configuredButtons,
  };
}

  getTooltip(params: any): string {
    if (!params.node?.data) {
      return '';
    }

    const columnDefs = params.api.getColumnDefs();
    if (!columnDefs) {
      return '';
    }

    return columnDefs
      .filter((col: any) => !col.hide && col.field && col.headerName)
      .map((col: any) => {
        const value = params.node.data[col.field];

        if (col.field === 'updatedAt') {
          const formattedDate = value
            ? this.datePipe.transform(value, 'dd MMM yyyy, h:mm a')
            : null;
          return `${col.headerName}: ${formattedDate ?? '—'}`;
        }

        return `${col.headerName}: ${value === null || value === undefined || value === '' ? '—' : value
          }`;
      })
      .join('\n');
  }

  private applyPreSelection(): void {
    if (!this.gridApi || !this.enableRowSelection || !this.selectedRowKeys?.length) {
      return;
    }

    this.gridApi.forEachNode(node => {
      const rowId = node.data?.[this.rowKey];
      if (this.selectedRowKeys.includes(rowId)) {
        node.setSelected(true);
      }
    });
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.rebuildColumns();

    setTimeout(() => {
      this.applyPreSelection();
    }, 0);
  }

  onActionClicked(action: string, rowIndex: number, rowData: any): void {
    this.actionClicked.emit({ action, rowIndex, rowData });
  }
}